import React, { createContext, useContext, useState, useEffect } from 'react';
import { apiClient } from '../services/api';

interface AuthContextType {
  token: string | null;
  setToken: (token: string, refreshToken?: string, expiresIn?: number) => void;
  clearToken: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
  refreshAuthToken: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

// Default credentials for automatic login (from environment variables)
const DEFAULT_CREDENTIALS = {
  username: import.meta.env.VITE_DEFAULT_USERNAME,
  password: import.meta.env.VITE_DEFAULT_PASSWORD,
  subdomain: import.meta.env.VITE_DEFAULT_SUBDOMAIN,
};

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [token, setTokenState] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [_tokenExpiry, setTokenExpiry] = useState<number | null>(null);
  const [refreshTimeoutId, setRefreshTimeoutId] = useState<NodeJS.Timeout | null>(null);

  // Helper function to perform token refresh
  const performTokenRefresh = async (currentRefreshToken: string): Promise<boolean> => {
    try {
      const subdomain = localStorage.getItem('subdomain') || DEFAULT_CREDENTIALS.subdomain || 'beta';
      const apiUrl = import.meta.env.VITE_BFF_API_URL;
      const response = await fetch(`${apiUrl}/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          refresh_token: currentRefreshToken,
          subdomain 
        }),
      });

      if (!response.ok) {
        throw new Error(`Token refresh failed: ${response.statusText}`);
      }

      const data = await response.json();
      const expiry = Date.now() + (data.expires_in * 1000);
      
      setTokenState(data.access_token);
      setRefreshToken(data.refresh_token || currentRefreshToken);
      setTokenExpiry(expiry);
      
      localStorage.setItem('auth_token', data.access_token);
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      localStorage.setItem('token_expiry', expiry.toString());
      
      apiClient.setToken(data.access_token);
      console.log('✅ Token refreshed successfully! New token expires in:', data.expires_in, 'seconds');
      
      // Schedule next refresh
      scheduleTokenRefresh(expiry);
      return true;
    } catch (error) {
      console.error('❌ Token refresh failed:', error);
      return false;
    }
  };

  // Helper function to schedule token refresh before expiry
  const scheduleTokenRefresh = (expiry: number) => {
    // Clear any existing timeout
    if (refreshTimeoutId) {
      clearTimeout(refreshTimeoutId);
    }

    // Schedule refresh 5 minutes before expiry
    const refreshTime = expiry - Date.now() - (5 * 60 * 1000);
    
    if (refreshTime > 0) {
      const timeoutId = setTimeout(async () => {
        const currentRefreshToken = localStorage.getItem('refresh_token');
        if (currentRefreshToken) {
          console.log('🔄 Auto-refreshing token...');
          await performTokenRefresh(currentRefreshToken);
        }
      }, refreshTime);
      
      setRefreshTimeoutId(timeoutId);
      console.log(`⏰ Token refresh scheduled in ${Math.round(refreshTime / 1000 / 60)} minutes`);
    }
  };

  useEffect(() => {
    const initializeAuth = async () => {
      // Set up automatic token refresh callback
      apiClient.setRefreshTokenCallback(async () => {
        const currentRefreshToken = localStorage.getItem('refresh_token');
        if (currentRefreshToken) {
          return performTokenRefresh(currentRefreshToken);
        }
        return false;
      });

      // Try to load token from localStorage
      const storedToken = localStorage.getItem('auth_token');
      const storedRefreshToken = localStorage.getItem('refresh_token');
      const storedExpiry = localStorage.getItem('token_expiry');
      
      if (storedToken && storedRefreshToken) {
        const expiry = storedExpiry ? parseInt(storedExpiry, 10) : null;
        
        // Check if token is expired
        if (expiry && Date.now() >= expiry) {
          console.log('🔄 Token expired, attempting refresh...');
          try {
            await performTokenRefresh(storedRefreshToken);
            setIsLoading(false);
            return;
          } catch (error) {
            console.log('❌ Token refresh failed, performing new login');
            // Fall through to auto-login
          }
        } else {
          setTokenState(storedToken);
          setRefreshToken(storedRefreshToken);
          setTokenExpiry(expiry);
          apiClient.setToken(storedToken);
          setIsLoading(false);
          console.log('✅ Using stored authentication token');
          
          // Schedule token refresh before expiry
          if (expiry) {
            scheduleTokenRefresh(expiry);
          }
          return;
        }
      }

      // No token found or refresh failed, perform automatic login with default credentials
      try {
        console.log('🔑 No token found, performing automatic login...');
        const apiUrl = import.meta.env.VITE_BFF_API_URL;
        const response = await fetch(`${apiUrl}/login`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(DEFAULT_CREDENTIALS),
        });

        if (!response.ok) {
          throw new Error(`Auto-login failed: ${response.statusText}`);
        }

        const data = await response.json();
        const expiry = Date.now() + (data.expires_in * 1000);
        
        setTokenState(data.access_token);
        setRefreshToken(data.refresh_token || null);
        setTokenExpiry(expiry);
        
        localStorage.setItem('auth_token', data.access_token);
        if (data.refresh_token) {
          localStorage.setItem('refresh_token', data.refresh_token);
        }
        localStorage.setItem('token_expiry', expiry.toString());
        if (DEFAULT_CREDENTIALS.subdomain) {
          localStorage.setItem('subdomain', DEFAULT_CREDENTIALS.subdomain);
        }
        
        apiClient.setToken(data.access_token);
        console.log('✅ Auto-login successful! Token expires in:', data.expires_in, 'seconds');
        console.log('💡 Use window.devTools to override credentials if needed');
        
        // Schedule token refresh before expiry
        scheduleTokenRefresh(expiry);
      } catch (error) {
        console.error('❌ Auto-login failed:', error);
        console.log('💡 Use window.devTools.login() to manually authenticate');
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const setToken = (newToken: string, newRefreshToken?: string, expiresIn?: number) => {
    const expiry = expiresIn ? Date.now() + (expiresIn * 1000) : null;
    
    setTokenState(newToken);
    setRefreshToken(newRefreshToken || null);
    setTokenExpiry(expiry);
    
    localStorage.setItem('auth_token', newToken);
    if (newRefreshToken) {
      localStorage.setItem('refresh_token', newRefreshToken);
    }
    if (expiry) {
      localStorage.setItem('token_expiry', expiry.toString());
      scheduleTokenRefresh(expiry);
    }
    
    apiClient.setToken(newToken);
  };

  const clearToken = () => {
    if (refreshTimeoutId) {
      clearTimeout(refreshTimeoutId);
    }
    
    setTokenState(null);
    setRefreshToken(null);
    setTokenExpiry(null);
    setRefreshTimeoutId(null);
    
    localStorage.removeItem('auth_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('token_expiry');
    localStorage.removeItem('subdomain');
    
    apiClient.clearToken();
  };

  const refreshAuthToken = async (): Promise<boolean> => {
    const currentRefreshToken = refreshToken || localStorage.getItem('refresh_token');
    if (!currentRefreshToken) {
      console.error('❌ No refresh token available');
      return false;
    }
    return performTokenRefresh(currentRefreshToken);
  };

  const value: AuthContextType = {
    token,
    setToken,
    clearToken,
    isAuthenticated: !!token,
    isLoading,
    refreshAuthToken,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
